import { lazy, Suspense, useEffect, useLayoutEffect } from 'react';

import { MdError } from 'react-icons/md';
import {
  createBrowserRouter,
  Navigate,
  Outlet,
  RouterProvider,
  ScrollRestoration,
  useLocation,
} from 'react-router-dom';

import InsightsHubLayout from './components/InsightsHubLayout';
import LoadingSpinner from './components/LoadingSpinner';
import MessagePage from './components/MessagePage';
import { ProtectedRoute } from './components/ProtectedRoute';
import { page } from './services/analytics';
import { withFaroRouterInstrumentation } from './services/faro/faroRouter';
import Sentry from './services/sentry';

// Loading component for page modules
const PageLoader = () => <LoadingSpinner fullScreen />;

// Wrapper component for lazy loaded routes
const LazyRoute = ({
  Component,
  ...props
}: {
  Component: React.LazyExoticComponent<React.ComponentType<any>>;
  [key: string]: any;
}) => (
  <Suspense fallback={<PageLoader />}>
    <Component {...props} />
  </Suspense>
);

// Lazy load all route components
const Home = lazy(() => import('./pages/home/Home').then((m) => ({ default: m.Home })));
const Contact = lazy(() => import('./pages/contact/Contact').then((m) => ({ default: m.Contact })));
const Project = lazy(() => import('./pages/project/Project').then((m) => ({ default: m.Project })));
const EnvironmentalAndSocialRisks = lazy(() =>
  import('./pages/project/environmental_and_social_risks/EnvironmentalAndSocialRisks').then(
    (m) => ({ default: m.EnvironmentalAndSocialRisks })
  )
);
const ExecutiveSummary = lazy(() =>
  import('./pages/project/executive_summary/ExecutiveSummary').then((m) => ({
    default: m.ExecutiveSummary,
  }))
);
const GhgRating = lazy(() =>
  import('./pages/project/ghg_rating/GHGRating').then((m) => ({ default: m.GhgRating }))
);
const RelatedContent = lazy(() =>
  import('./pages/project/RelatedContent').then((m) => ({ default: m.RelatedContent }))
);
const SDGRating = lazy(() =>
  import('./pages/project/sdg_rating/SDGRating').then((m) => ({ default: m.SDGRating }))
);
const RetireeInsights = lazy(() =>
  import('./pages/retireeInsights/RetireeInsights').then((m) => ({ default: m.RetireeInsights }))
);
const RetireeInsightsDrillDown = lazy(() =>
  import('./pages/retireeDrillDown/RetireeInsightsDrillDown').then((m) => ({
    default: m.RetireeInsightsDrillDown,
  }))
);
const Search = lazy(() => import('./pages/search/Search').then((m) => ({ default: m.Search })));
const WhereToBuy = lazy(() =>
  import('./pages/whereToBuy/WhereToBuy').then((m) => ({ default: m.WhereToBuy }))
);
const SignUp = lazy(() => import('./pages/signup/SignUp').then((m) => ({ default: m.SignUp })));
const InsightsHub = lazy(() =>
  import('./pages/insightsHub/InsightsHub').then((m) => ({ default: m.InsightsHub }))
);
const InsightsHubArticle = lazy(() =>
  import('./pages/insightsHubArticle/InsightsHubArticle').then((m) => ({
    default: m.InsightsHubArticle,
  }))
);
const InsightsHubCategory = lazy(() =>
  import('./pages/insightsHubCategory/InsightsHubCategory').then((m) => ({
    default: m.InsightsHubCategory,
  }))
);
const InsightsHubPinnedPost = lazy(() =>
  import('./pages/insightsHubArticle/InsightsHubPinnedPost').then((m) => ({
    default: m.InsightsHubPinnedPost,
  }))
);
const InsightsHubResearchUpdate = lazy(() =>
  import('./pages/insightsHubResearchUpdate/InsightsHubResearchUpdate').then((m) => ({
    default: m.InsightsHubResearchUpdate,
  }))
);
const Programs = lazy(() =>
  import('./pages/programs/Programs').then((m) => ({ default: m.Programs }))
);
const AccountRequest = lazy(() =>
  import('./pages/accountRequest/AccountRequest').then((m) => ({ default: m.AccountRequest }))
);
const DeveloperPortal = lazy(() =>
  import('./pages/developerPortal/DeveloperPortal').then((m) => ({ default: m.DeveloperPortal }))
);
const Upgrade = lazy(() => import('./pages/upgrade/Upgrade').then((m) => ({ default: m.Upgrade })));
const Compass = lazy(() => import('./pages/compass/Compass').then((m) => ({ default: m.Compass })));
const ProjectType = lazy(() =>
  import('./pages/compass/components/projectType/ProjectType').then((m) => ({
    default: m.ProjectType,
  }))
);
const RootOverview = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/Overview').then((m) => ({
    default: m.Overview,
  }))
);
const RootSupplyAndDemand = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/SupplyAndDemand').then((m) => ({
    default: m.SupplyAndDemand,
  }))
);
const RootTopGHGRisks = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/TopGHGRisks').then((m) => ({
    default: m.TopGHGRisks,
  }))
);

const RootMethodologyOverview = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/methodologyDetail/Overview').then(
    (m) => ({
      default: m.Overview,
    })
  )
);
const RootTopGHGRisksDetail = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/TopGHGRisksDetail').then((m) => ({
    default: m.TopGHGRisksDetail,
  }))
);

const MethodologyDetail = lazy(() =>
  import('./pages/compass/components/projectType/MethodologyDetail').then((m) => ({
    default: m.MethodologyDetail,
  }))
);
const RootMethodologyArea = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/methodologyDetail/MethodologyArea').then(
    (m) => ({ default: m.MethodologyArea })
  )
);
const RootScope = lazy(() =>
  import('./pages/compass/components/projectType/Renderer/methodologyDetail/Scope').then((m) => ({
    default: m.Scope,
  }))
);
const RootCorsia = lazy(() =>
  import(
    './pages/compass/components/projectType/Renderer/methodologyDetail/CorsiaEligibility'
  ).then((m) => ({ default: m.CorsiaEligibility }))
);
const FreeDashboard = lazy(() => import('./pages/freeDashboard/FreeDashboard'));
const PricingAndSupply = lazy(() =>
  import('./pages/project/pricing_and_supply/PricingAndSupply').then((m) => ({
    default: m.PricingAndSupply,
  }))
);
const Register = lazy(() =>
  import('./pages/register/Register').then((m) => ({ default: m.Register }))
);
const SupplyLink = lazy(() =>
  import('./pages/supplylink/SupplyLink').then((m) => ({ default: m.SupplyLink }))
);
const ExpertAdvisory = lazy(() =>
  import('./pages/expertAdvisory/ExpertAdvisory').then((m) => ({ default: m.ExpertAdvisory }))
);
const ExpertAdvisoryLearnMore = lazy(() =>
  import('./pages/expertAdvisory/ExpertAdvisoryLearnMore').then((m) => ({
    default: m.ExpertAdvisoryLearnMore,
  }))
);

let previousPath = '/';
const NavigateToProfileSettings = () => {
  useLayoutEffect(() => {
    window.location.href = process.env.IS_PRODUCTION_APP
      ? 'https://auth.calyxglobal.com/account'
      : 'https://auth.calyxstaging.com/account';
  }, []);
  return <></>;
};

const AppLayout = () => {
  const location = useLocation();

  useEffect(() => {
    page();
    if (!(location.state as { preventScrollReset?: boolean })?.preventScrollReset) {
      const layout = document.getElementById('layout');
      layout?.scrollTo(0, 0);
    }
  }, [location]);

  return (
    <Sentry.ErrorBoundary
      fallback={({ resetError }) => (
        <MessagePage
          title="Oops, that's our bad!"
          description="We're not exactly sure what happened, rest assured we've been notified about this issue and this will be fixed soon!"
          Icon={MdError}
          hasAction
          resetError={resetError}
        />
      )}
    >
      <ScrollRestoration />
      <Outlet />
    </Sentry.ErrorBoundary>
  );
};

const loaderCallback = ({ request }) => {
  const prev = previousPath;
  previousPath = new URL(request.url).pathname;
  return { prevPath: prev };
};

const router = createBrowserRouter([
  {
    path: '/',
    element: <AppLayout />,
    errorElement: (
      <MessagePage
        title='Page not found'
        description='The page you are looking for doesn’t exist or has been moved.'
        hasAction
      />
    ),
    children: [
      {
        path: '/',
        element: <ProtectedRoute />,
        children: [
          {
            path: `/`,
            element: <LazyRoute Component={Home} />,
          },
          {
            path: '/login',
            element: <Navigate to='/' />,
          },
          {
            path: '/developer-portal',
            element: <LazyRoute Component={DeveloperPortal} />,
          },
          {
            path: 'research-hub',
            element: <InsightsHubLayout />,
            children: [
              {
                path: '',
                element: <LazyRoute Component={InsightsHub} />,
              },
              {
                path: 'pinned/:slug',
                element: <LazyRoute Component={InsightsHubPinnedPost} />,
              },
              {
                path: ':category',
                element: <LazyRoute Component={InsightsHubCategory} />,
              },
              {
                path: ':category/:slug',
                element: <LazyRoute Component={InsightsHubArticle} />,
              },
              {
                path: 'research_update/:slug',
                element: <LazyRoute Component={InsightsHubResearchUpdate} />,
              },
            ],
          },
          // redirect old blog/resources/notes to research-hub start
          {
            path: 'blog_detail_dist',
            element: (
              <Navigate
                to={`/research-hub/pinned/current-calyx-risk-ratings-distribution${window.location.search}`}
                replace
              />
            ),
          },
          {
            path: 'blog',
            element: <Navigate to={`/research-hub${window.location.search}`} replace />,
          },
          {
            path: 'resource',
            element: <Navigate to={`/research-hub${window.location.search}`} replace />,
          },
          {
            path: 'note',
            element: <Navigate to={`/research-hub${window.location.search}`} replace />,
          },
          // redirect old blog/resources/notes to research-hub end
          {
            path: `/credit-ratings`,
            element: <LazyRoute Component={Search} />,
            loader: loaderCallback,
          },
          // redirect to new search page
          {
            path: `/search`,
            element: <Navigate to={`/credit-ratings`} replace />,
            loader: loaderCallback,
          },
          {
            path: `/supply-link`,
            element: <Navigate to={`/supply-bridge`} replace />,
          },
          // redirect to supply bridge
          {
            path: `/supply-bridge`,
            element: <LazyRoute Component={SupplyLink} />,
          },
          {
            path: `/compass`,
            element: <LazyRoute Component={Compass} />,
          },
          {
            path: '/compass/:projectType',
            element: <LazyRoute Component={ProjectType} />,
            children: [
              {
                path: 'overview',
                element: <LazyRoute Component={RootOverview} />,
                handle: { breadcrumb: '', activeTab: 'overview' },
              },
              {
                path: 'supply-and-demand',
                element: <LazyRoute Component={RootSupplyAndDemand} />,
                handle: { breadcrumb: 'Supply and demand', activeTab: 'supply-and-demand' },
              },
              {
                path: 'top-ghg-risks',
                element: <LazyRoute Component={RootTopGHGRisks} />,
                handle: { breadcrumb: 'Top GHG risks' },
                children: [
                  {
                    path: '',
                    element: <Navigate to='additionality' replace />,
                  },
                  {
                    path: 'additionality',
                    element: <LazyRoute Component={RootTopGHGRisksDetail} />,
                    handle: {
                      breadcrumb: 'Additionality',
                      activeTab: 'additionality',
                      activeTabKey: 'additionality',
                      parentTab: 'top-ghg-risks',
                    },
                  },
                  {
                    path: 'over-crediting',
                    element: <LazyRoute Component={RootTopGHGRisksDetail} />,
                    handle: {
                      breadcrumb: 'Over-crediting',
                      activeTab: 'over-crediting',
                      activeTabKey: 'overCrediting',
                      parentTab: 'top-ghg-risks',
                    },
                  },
                  {
                    path: 'permanence',
                    element: <LazyRoute Component={RootTopGHGRisksDetail} />,
                    handle: {
                      breadcrumb: 'Permanence',
                      activeTab: 'permanence',
                      activeTabKey: 'permanence',
                      parentTab: 'top-ghg-risks',
                    },
                  },
                  {
                    path: 'overlapping-claims',
                    element: <LazyRoute Component={RootTopGHGRisksDetail} />,
                    handle: {
                      breadcrumb: 'Overlapping claims',
                      activeTab: 'overlapping-claims',
                      activeTabKey: 'overLappingClaims',
                      parentTab: 'top-ghg-risks',
                    },
                  },
                ],
              },
              {
                path: 'methodologies',
                element: <LazyRoute Component={MethodologyDetail} />,
                handle: { breadcrumb: 'Methodologies' },
                children: [
                  {
                    path: '',
                    element: <Navigate to='overview' replace />,
                  },
                  {
                    path: 'overview',
                    element: <LazyRoute Component={RootMethodologyOverview} />,
                    handle: {
                      breadcrumb: 'Overview',
                      activeTab: 'overview',
                      parentTab: 'methodologies',
                    },
                  },
                  {
                    path: 'scope',
                    element: <LazyRoute Component={RootScope} />,
                    handle: {
                      breadcrumb: 'Scope',
                      activeTab: 'scope',
                      activeTabKey: 'scope',
                      parentTab: 'methodologies',
                    },
                  },
                  {
                    path: 'additionality',
                    element: <LazyRoute Component={RootMethodologyArea} />,
                    handle: {
                      breadcrumb: 'Additionality',
                      activeTab: 'additionality',
                      activeTabKey: 'additionality',
                      parentTab: 'methodologies',
                    },
                  },
                  {
                    path: 'over-crediting',
                    element: <LazyRoute Component={RootMethodologyArea} />,
                    handle: {
                      breadcrumb: 'Over-crediting',
                      activeTab: 'over-crediting',
                      activeTabKey: 'overCrediting',
                      parentTab: 'methodologies',
                    },
                  },
                  {
                    path: 'permanence',
                    element: <LazyRoute Component={RootMethodologyArea} />,
                    handle: {
                      breadcrumb: 'Permanence',
                      activeTab: 'permanence',
                      activeTabKey: 'permanence',
                      parentTab: 'methodologies',
                    },
                  },
                  {
                    path: 'overlapping-claims',
                    element: <LazyRoute Component={RootMethodologyArea} />,
                    handle: {
                      breadcrumb: 'Overlapping claims',
                      activeTab: 'overlapping-claims',
                      activeTabKey: 'overLappingClaims',
                      parentTab: 'methodologies',
                    },
                  },
                  {
                    path: 'corsia',
                    element: <LazyRoute Component={RootCorsia} />,
                    handle: {
                      breadcrumb: 'CORSIA eligibility',
                      activeTab: 'corsia',
                      parentTab: 'methodologies',
                    },
                  },
                ],
              },
            ],
          },
          {
            path: `/expert-advisory`,
            element: <LazyRoute Component={ExpertAdvisory} />,
          },
          {
            path: `/expert-advisory/:service_name`,
            element: <LazyRoute Component={ExpertAdvisoryLearnMore} />,
          },
          {
            path: `/`,
            element: <LazyRoute Component={Project} />,
            children: [
              {
                path: 'executive_summary',
                element: <LazyRoute Component={ExecutiveSummary} />,
                loader: loaderCallback,
              },
              {
                path: 'ghg_rating',
                element: <LazyRoute Component={GhgRating} />,
                handle: { breadcrumb: 'GHG rating' },
              },
              {
                path: 'esr_rating',
                element: <LazyRoute Component={EnvironmentalAndSocialRisks} />,
                handle: { breadcrumb: 'ESR rating' },
                children: [
                  {
                    path: 'detailed_risk_findings',
                    element: <LazyRoute Component={EnvironmentalAndSocialRisks} />,
                  },
                  {
                    path: 'project_comparison',
                    element: <LazyRoute Component={EnvironmentalAndSocialRisks} />,
                  },
                  {
                    path: 'further_research',
                    element: <LazyRoute Component={EnvironmentalAndSocialRisks} />,
                  },
                  {
                    path: 'sources_and_methodology',
                    element: <LazyRoute Component={EnvironmentalAndSocialRisks} />,
                  },
                ],
              },
              {
                path: 'ess_rating',
                element: <Navigate to={`/esr_rating${window.location.search}`} replace />,
              },
              {
                path: 'ess_rating/detailed_risk_findings',
                element: (
                  <Navigate
                    to={`/esr_rating/detailed_risk_findings${window.location.search}`}
                    replace
                  />
                ),
              },
              {
                path: 'ess_rating/project_comparison',
                element: (
                  <Navigate
                    to={`/esr_rating/project_comparison${window.location.search}`}
                    replace
                  />
                ),
              },
              {
                path: 'ess_rating/further_research',
                element: (
                  <Navigate to={`/esr_rating/further_research${window.location.search}`} replace />
                ),
              },
              {
                path: 'ess_rating/sources_and_methodology',
                element: (
                  <Navigate
                    to={`/esr_rating/sources_and_methodology${window.location.search}`}
                    replace
                  />
                ),
              },
              {
                path: 'related_content',
                element: <LazyRoute Component={RelatedContent} />,
                handle: { breadcrumb: 'Related content' },
              },
              {
                path: 'sdg_rating',
                element: <LazyRoute Component={SDGRating} />,
                handle: { breadcrumb: 'SDG rating' },
              },
              {
                path: 'pricing_and_supply',
                element: <LazyRoute Component={PricingAndSupply} />,
                handle: { breadcrumb: 'Pricing & supply' },
              },
            ],
          },
          {
            path: `/where_to_buy`,
            element: <LazyRoute Component={WhereToBuy} />,
          },
          {
            path: 'retirement_insights',
            element: <LazyRoute Component={RetireeInsights} />,
          },
          {
            path: 'retirement_insights/:retiree',
            element: <LazyRoute Component={RetireeInsightsDrillDown} />,
          },
          {
            path: 'contact',
            element: <LazyRoute Component={Contact} />,
          },
          {
            path: 'profile',
            element: <NavigateToProfileSettings />,
          },
          {
            path: 'dashboard',
            element: <Navigate to='/' />,
          },
          {
            path: '/programs',
            element: <LazyRoute Component={Programs} />,
          },
          {
            path: '/upgrade',
            element: <LazyRoute Component={Upgrade} />,
          },
        ],
      },
      {
        path: '/signup',
        element: <LazyRoute Component={SignUp} />, // account signup form for secondnature universities
      },
      {
        path: '/account-signup',
        element: <LazyRoute Component={SignUp} signupType='b2b2b' />, // generic account sign up form for all users coming from B2B2B
      },
      {
        path: '/account-request',
        element: <LazyRoute Component={AccountRequest} />,
      },
      {
        path: '/register',
        element: <LazyRoute Component={Register} />,
      },
      {
        path: '/free-dashboard',
        element: <LazyRoute Component={FreeDashboard} />,
      },
    ],
  },
]);

// Initialize Faro with router instrumentation
const instrumentedRouter = withFaroRouterInstrumentation(router);

export const Routes = () => {
  return <RouterProvider router={instrumentedRouter} />;
};
